import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "styles.module.css";
import { useTranslation } from "react-i18next";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import {
	getRegistrationOverviewApi,
	getRegistrationPackageOverviewApi,
	RegistrationOverviewParams,
} from "api/registration";
import { getRegistrationLineOverviewApi } from "api/registration";
import { registrationOverviewGridColumns } from "./utils/OverviewGridColumns";
import OverviewFilter from "./utils/OverviewFilter";
import { useSearchParams } from "react-router";

import { PageTypePath } from "utils/pages";
import RegistrationOverviewMobileList from "./RegistrationMobileList";
import { RegistrationOverview } from "interface/registration";
import { useInfoStore } from "store/error";
import { COLOR } from "utils/constant";
type OverviewType = "pakke" | "hode" | "linje";

const RegistrationOverviewPage: React.FC = () => {
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const { setInfo, resetStore } = useInfoStore();

	// const [pageAccess, setPageAccess] = useState<PageAccessByMenu | null>(null);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1600);
	const [mobileData, setMobileData] = useState<RegistrationOverview[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const [overviewType, setOverviewType] = useState<OverviewType>(
		searchParams.get("type") as OverviewType,
	);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const method = useMemo(() => {
		if (overviewType === "pakke") return getRegistrationPackageOverviewApi;
		if (overviewType === "hode") return getRegistrationOverviewApi;
		if (overviewType === "linje") return getRegistrationLineOverviewApi;
		return undefined;
	}, [overviewType]);

	const [filterOptions, setFilterOptions] =
		useState<RegistrationOverviewParams>({
			registrationTimeFrom: new Date(),
			registrationTimeTo: new Date(),
			landingTimeFrom: null,
			landingTimeTo: null,
		});

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 1600);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (
			overviewType === "pakke" ||
			overviewType === "hode" ||
			overviewType === "linje"
		) {
			// method = getRegistrationPackageOverviewApi;
			return;
		}

		setInfo({
			infoType: "Warning",
			title: t("Wrong") + " " + t("Overview") + " " + t("Type"),
			description:
				t("Wrong") +
				" " +
				t("RegistrationOverview") +
				" " +
				t("Type") +
				" " +
				t("Expected") +
				" pakke " +
				t("Or") +
				" hode " +
				t("Or") +
				" linje " +
				t("Got") +
				" " +
				overviewType,
			show: true,
			actions: [
				{
					text: t("Ok"),
					btn: () => {
						//console.log("Delete cancelled");
						resetStore();
					},
					textColor: COLOR.StatusRed,
				},
			],
		});
	}, [overviewType, resetStore, setInfo, t]);

	useEffect(() => {
		const type = searchParams.get("type") as OverviewType;
		setOverviewType(type);
		setForceUpdate((prev) => !prev);
	}, [searchParams]);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		setMobileLoading(true);

		if (method) {
			method(filterOptions)
				.then((res) => {
					setMobileData(res);
				})
				.catch((err) => {
					setMobileData([]);
				})
				.finally(() => {
					setMobileLoading(false);
				});
		}
	}, [isMobileView, filterOptions, method]);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const handleLandingNote = (props: any) => {
		window.open(
			`/${PageTypePath.FisheryDepartmentLandingNotes}/${props.registrationId}`,
		);
	};

	const handleSalesNote = (props: any) => {
		window.open(
			`/${PageTypePath.FisheryDepartmentSalesNotes}/${props.registrationId}`,
		);
	};

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("RegistrationOverview") + " - " + overviewType}
				</legend>
			</div>
			<div>
				<OverviewFilter
					filterOptions={filterOptions}
					setFilterOptions={setFilterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
				/>
			</div>
			<div style={{ marginTop: 30 }}>
				{isMobileView ? (
					<RegistrationOverviewMobileList
						items={mobileData}
						loading={mobileLoading}
						onViewLandingNote={handleLandingNote}
						onViewSalesNote={handleSalesNote}
						onLoadMore={() => undefined}
					/>
				) : (
					<GridWithGroupPaging
						gridColumns={registrationOverviewGridColumns({
							onViewLandingNote: handleLandingNote,
							onViewSalesNote: handleSalesNote,
							overviewType: overviewType,
						})}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={method}
						forceUpdate={forceUpdate}
						filter={filterOptions}
						itemAboveHeight={100}
						pageable={false}
					/>
				)}
			</div>
		</div>
	);
};

export default RegistrationOverviewPage;
